import { defineStore } from 'pinia'
import storageService from '@/services/storage.service'

export const useStorageQuotaStore = defineStore({
  id: 'storageQuota',

  state: () => ({
    usage: null,
    config: null,
    syncStatus: null,
    syncFiles: [],
    syncFilesTotal: 0,
    syncLogs: [],
    loading: false,
    configLoading: false,
    syncLoading: false,
    error: null,

    // From response headers (set by axios interceptor)
    warningPercent: null,
    isOverage: false,
    graceExpires: null,
  }),

  getters: {
    usagePercent: state => state.usage?.usage_percent || 0,

    isWarning: state => state.usage?.is_warning || false,

    isBlocked: state => state.usage?.is_blocked || false,

    localUsedFormatted: state => state.usage?.formatted?.used || '0 B',

    limitFormatted: state => state.usage?.formatted?.limit || '0 B',

    syncedTotalFormatted: state => state.usage?.formatted?.synced_total || '0 B',

    fileCount: state => state.usage?.file_count || 0,

    hasNasConfig: state => !!state.config,

    isNasEnabled: state => state.config?.is_enabled || false,

    isSyncing: state => state.syncStatus?.is_syncing || false,

    showWarningBanner: state => {
      if (!state.usage) return false
      return state.usage.is_warning && !state.usage.is_overage
    },

    showOverageBanner: state => {
      if (!state.usage) return false
      return state.usage.is_overage || state.usage.is_blocked
    },
  },

  actions: {
    async loadUsage() {
      this.loading = true
      this.error = null
      try {
        const { data } = await storageService.getUsage()
        this.usage = data.data || null
      } catch (err) {
        this.error = err.response?.data?.message || 'Gagal memuat data penyimpanan'
        this.usage = null
      } finally {
        this.loading = false
      }
    },

    async loadConfig() {
      this.configLoading = true
      try {
        const { data } = await storageService.getConfig()
        this.config = data.data || null
      } catch (err) {
        if (err.response?.status !== 404) {
          this.error = err.response?.data?.message || 'Gagal memuat konfigurasi NAS'
        }
        this.config = null
      } finally {
        this.configLoading = false
      }
    },

    async saveConfig(configData) {
      this.configLoading = true
      try {
        const { data } = await storageService.saveConfig(configData)
        this.config = data.data || null
        return data
      } catch (err) {
        throw err
      } finally {
        this.configLoading = false
      }
    },

    async testConnection() {
      try {
        const { data } = await storageService.testConnection()
        if (this.config) {
          this.config.connection_verified = !!data.success
        }
        return data
      } catch (err) {
        // 422 response still has useful message in response body
        if (err.response && err.response.data) {
          return err.response.data
        }
        throw err
      }
    },

    async toggleNas() {
      try {
        const { data } = await storageService.toggleNas()
        if (this.config) {
          this.config.is_enabled = data.data?.is_enabled
        }
        return data
      } catch (err) {
        throw err
      }
    },

    async deleteConfig() {
      try {
        await storageService.deleteConfig()
        this.config = null
      } catch (err) {
        throw err
      }
    },

    async loadSyncStatus() {
      this.syncLoading = true
      try {
        const { data } = await storageService.getSyncStatus()
        this.syncStatus = data.data || null
      } catch (err) {
        this.error = err.response?.data?.message || 'Gagal memuat status sinkronisasi'
        this.syncStatus = null
      } finally {
        this.syncLoading = false
      }
    },

    async loadSyncFiles(params = {}) {
      try {
        const { data } = await storageService.getSyncFiles(params)
        const paginated = data.data || {}
        this.syncFiles = paginated.data || []
        this.syncFilesTotal = paginated.total || 0
        return paginated
      } catch (err) {
        this.error = err.response?.data?.message || 'Gagal memuat daftar file'
      }
    },

    async loadSyncLogs(params = {}) {
      try {
        const { data } = await storageService.getSyncLogs(params)
        this.syncLogs = data.data?.data || data.data || []
        return data.data
      } catch (err) {
        this.error = err.response?.data?.message || 'Gagal memuat riwayat sinkronisasi'
      }
    },

    async retrySync() {
      try {
        const { data } = await storageService.retrySync()
        return data
      } catch (err) {
        throw err
      }
    },

    async triggerSync() {
      try {
        const { data } = await storageService.triggerSync()
        return data
      } catch (err) {
        throw err
      }
    },

    // Called from axios interceptor when storage headers are present
    updateFromHeaders(headers) {
      if (headers['x-storage-warning']) {
        this.warningPercent = parseFloat(headers['x-storage-usage-percent']) || null
      }
      if (headers['x-storage-overage']) {
        this.isOverage = true
        this.graceExpires = headers['x-storage-grace-expires'] || null
      }
    },
  },
})
